import React from 'react';
import { Sparkles, FlaskConical, Zap } from 'lucide-react';
import { DESIGN } from './DesignSystem';

// ================================================
// LAB BADGES - Used across all pages for LAB-created items
// ================================================

// Primary "AI Generated" badge - teal gradient with spark icon
export const AIGeneratedBadge = ({ size = 'default' }) => {
  const sizes = {
    sm: { px: 'px-2', py: 'py-0.5', text: 'text-[10px]', icon: 'w-2.5 h-2.5', gap: 'gap-1' },
    default: { px: 'px-2.5', py: 'py-1', text: 'text-xs', icon: 'w-3 h-3', gap: 'gap-1.5' },
    lg: { px: 'px-3', py: 'py-1.5', text: 'text-sm', icon: 'w-4 h-4', gap: 'gap-2' }
  };
  
  const s = sizes[size] || sizes.default;
  
  return (
    <span 
      className={`inline-flex items-center ${s.gap} ${s.px} ${s.py} ${s.text} font-bold rounded-full`}
      style={{ 
        background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
        color: 'white',
        boxShadow: '0 0 12px rgba(20, 184, 166, 0.4)'
      }}
      data-testid="ai-generated-badge"
    >
      <Sparkles className={s.icon} />
      AI Generated
    </span>
  );
};

// "LAB Origin" badge - indicates item came from LAB conversation
export const LabOriginBadge = ({ size = 'default' }) => {
  const sizes = {
    sm: { px: 'px-2', py: 'py-0.5', text: 'text-[10px]', icon: 'w-2.5 h-2.5', gap: 'gap-1' },
    default: { px: 'px-2.5', py: 'py-1', text: 'text-xs', icon: 'w-3 h-3', gap: 'gap-1.5' },
    lg: { px: 'px-3', py: 'py-1.5', text: 'text-sm', icon: 'w-4 h-4', gap: 'gap-2' }
  };
  
  const s = sizes[size] || sizes.default;
  
  return (
    <span 
      className={`inline-flex items-center ${s.gap} ${s.px} ${s.py} ${s.text} font-bold rounded-full`}
      style={{ 
        background: 'linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)',
        color: 'white',
        boxShadow: '0 0 12px rgba(20, 184, 166, 0.35)'
      }}
      data-testid="lab-origin-badge"
    >
      <FlaskConical className={s.icon} />
      LAB Origin
    </span>
  );
};

// "LAB Recommended" badge - for team role recommendations
export const LabRecommendedBadge = ({ size = 'default' }) => {
  const sizes = {
    sm: { px: 'px-2', py: 'py-0.5', text: 'text-[10px]', icon: 'w-2.5 h-2.5', gap: 'gap-1' },
    default: { px: 'px-2.5', py: 'py-1', text: 'text-xs', icon: 'w-3 h-3', gap: 'gap-1.5' },
    lg: { px: 'px-3', py: 'py-1.5', text: 'text-sm', icon: 'w-4 h-4', gap: 'gap-2' }
  };
  
  const s = sizes[size] || sizes.default;
  
  return (
    <span 
      className={`inline-flex items-center ${s.gap} ${s.px} ${s.py} ${s.text} font-bold rounded-full`}
      style={{ 
        background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
        color: 'white',
        boxShadow: '0 0 12px rgba(139, 92, 246, 0.35)'
      }}
      data-testid="lab-recommended-badge"
    >
      <Zap className={s.icon} />
      LAB Recommended
    </span>
  );
};

// Compact dot indicator for lists
export const LabIndicatorDot = ({ tooltip = true }) => (
  <div 
    className="relative w-3 h-3 rounded-full flex-shrink-0"
    style={{ 
      background: 'linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)',
      boxShadow: '0 0 8px rgba(20, 184, 166, 0.5)'
    }}
    title={tooltip ? 'Created from LAB conversation' : undefined}
    data-testid="lab-indicator-dot"
  >
    <div 
      className="absolute inset-0 rounded-full animate-ping"
      style={{ backgroundColor: '#14B8A6', opacity: 0.3 }}
    />
  </div>
);

// LAB source link - shows origin conversation
export const LabSourceLink = ({ conversationTitle = 'Q3 Growth Strategy Session', onClick }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-xs font-medium transition-all hover:underline"
    style={{ color: DESIGN.colors.primary }}
    data-testid="lab-source-link"
  >
    <FlaskConical className="w-3 h-3" />
    From: {conversationTitle}
  </button>
);

// Conversion info banner - shows when items were created
export const LabConversionBanner = ({ 
  date, 
  itemCount = 15, 
  onViewResults, 
  compact = false 
}) => {
  if (compact) {
    return (
      <div 
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
        style={{ 
          backgroundColor: `${DESIGN.colors.primary}10`,
          border: `1px solid ${DESIGN.colors.primary}30`
        }}
        data-testid="lab-conversion-banner-compact"
      >
        <LabIndicatorDot tooltip={false} />
        <span style={{ color: DESIGN.colors.textSecondary }}>
          Converted from LAB on {date}
        </span>
        {onViewResults && (
          <button 
            onClick={onViewResults}
            className="font-semibold hover:underline"
            style={{ color: DESIGN.colors.primary }}
          >
            View Results
          </button>
        )}
      </div>
    );
  }

  return (
    <div 
      className="px-4 py-3 rounded-xl flex items-center justify-between"
      style={{ 
        background: `linear-gradient(90deg, ${DESIGN.colors.primary}15 0%, transparent 100%)`,
        border: `1px solid ${DESIGN.colors.primary}30`
      }}
      data-testid="lab-conversion-banner"
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${DESIGN.colors.primary}20` }}
        >
          <FlaskConical className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
            Created from LAB conversation
          </p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
            {itemCount} items • {date}
          </p>
        </div>
      </div>
      {onViewResults && (
        <button
          onClick={onViewResults}
          className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:shadow-lg"
          style={{ 
            backgroundColor: DESIGN.colors.primary,
            color: 'white'
          }}
        >
          View Results
        </button>
      )}
    </div>
  );
};

// Export all components
export default {
  AIGeneratedBadge,
  LabOriginBadge,
  LabRecommendedBadge,
  LabIndicatorDot,
  LabSourceLink,
  LabConversionBanner
};
